/**
 * Create-server dialog. Servers are org-scoped named hosts — they don't yet
 * carry an access method (that lands when adapters do), so v1 captures the
 * minimum that's useful: name (slug derived), hostname, description.
 */
import { useState, type FormEvent } from 'react';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { useNavigate } from '@tanstack/react-router';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/Dialog.js';
import { Button } from '@/components/ui/Button.js';
import { Input } from '@/components/ui/Input.js';
import { Label } from '@/components/ui/Label.js';
import { api, ApiError, type PrincipalResponse } from '@/api/client.js';

type Membership = PrincipalResponse['memberships'][number];

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  memberships: Membership[];
  onCreated: () => void | Promise<void>;
}

export function CreateServerDialog({ open, onOpenChange, memberships, onCreated }: Props): JSX.Element {
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [hostname, setHostname] = useState('');
  const [description, setDescription] = useState('');
  const [orgId, setOrgId] = useState(memberships[0]?.orgId ?? '');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function reset() {
    setName('');
    setHostname('');
    setDescription('');
    setOrgId(memberships[0]?.orgId ?? '');
    setError(null);
    setSubmitting(false);
  }

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (!orgId) {
      setError('Pick an organisation to own this server.');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const created = await api.servers.create(orgId, {
        name,
        ...(hostname ? { hostname } : {}),
        ...(description ? { description } : {}),
      });
      toast.success(`Created ${created.name}`);
      await onCreated();
      onOpenChange(false);
      reset();
      await navigate({
        to: '/p/applications/servers/$slug',
        params: { slug: created.slug },
        search: { org: created.orgSlug },
      });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not create server.');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o) reset();
        onOpenChange(o);
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>New server</DialogTitle>
          <DialogDescription>
            A named host that components can be attached to. Access methods (SSH, docker,
            kubectl) come later — for now this is just the identity of the box.
          </DialogDescription>
        </DialogHeader>

        <form className="space-y-4" onSubmit={submit}>
          <div className="space-y-1.5">
            <Label htmlFor="org">Organisation</Label>
            <select
              id="org"
              value={orgId}
              onChange={(e) => setOrgId(e.target.value)}
              className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              {memberships.map((m) => (
                <option key={m.orgId} value={m.orgId}>
                  {m.orgName} (/{m.orgSlug})
                </option>
              ))}
            </select>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="name">Name</Label>
            <Input
              id="name"
              required
              autoFocus
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="web-01"
            />
            <p className="text-xs text-muted-foreground">A slug is derived automatically from the name.</p>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="hostname">Hostname</Label>
            <Input
              id="hostname"
              value={hostname}
              onChange={(e) => setHostname(e.target.value)}
              placeholder="web-01.acme.example"
            />
            <p className="text-xs text-muted-foreground">
              Optional. The reachable DNS name or IP. Doesn't need to be unique.
            </p>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="desc">Description</Label>
            <Input
              id="desc"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Optional"
            />
          </div>

          {error && (
            <p className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive">
              {error}
            </p>
          )}

          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={submitting || !name || !orgId}>
              {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Create server'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
